import { useState } from "react";
import { Download, LoaderCircle } from "lucide-react";
import { apiClient } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { downloadBlob } from "@/features/uploads/upload-shared";

/** `configuracao-<empresa>.xlsx` (ou `premissas-<empresa>.xlsx`, com
 * `prefix`), só ASCII — a mesma forma do nome que o servidor sugere no
 * `Content-Disposition`. */
export function configFilename(
  companyName: string | undefined,
  prefix = "configuracao"
): string {
  const slug = (companyName ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return slug ? `${prefix}-${slug}.xlsx` : `${prefix}.xlsx`;
}

/**
 * "Baixar configuração": a configuração completa da empresa numa planilha
 * de várias abas (a primeira metade da migração por planilha).
 *
 * Falha de AÇÃO não troca a página: o erro aparece aqui, ao lado do
 * botão, e a tela de Ajustes continua como estava.
 */
export function ConfigExportButton({
  token,
  companyName
}: {
  token: string;
  companyName?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setFailure(null);
    try {
      downloadBlob(
        await apiClient.downloadCompanyConfig(token),
        configFilename(companyName)
      );
    } catch (error) {
      setFailure(
        errorMessage(error, "Não foi possível baixar a configuração.")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ajustes-nav-export">
      <button
        type="button"
        className="ajustes-tool"
        aria-label="Baixar configuração"
        title="Todas as abas de configuração desta empresa numa planilha só"
        disabled={busy}
        onClick={() => void download()}
      >
        <span className="ajustes-tool-icon" aria-hidden="true">
          {busy ? <LoaderCircle className="spin" size={15} /> : <Download size={15} />}
        </span>
        <span className="ajustes-tool-copy" aria-hidden="true">
          <strong>Baixar configuração</strong>
          <small>Planilha com todas as abas</small>
        </span>
      </button>
      {failure && (
        <small className="ajustes-nav-export-error" role="alert">
          {failure}
        </small>
      )}
    </div>
  );
}
